import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useI18n } from "../I18nContext";

function pickL(o, lang, base = "label") {
  return (lang === "fr" && o[`${base}_fr`]) ? o[`${base}_fr`] : o[`${base}_en`] || o[`${base}_fr`] || "";
}

/* List */
export default function Surveys() {
  const { t, lang } = useI18n();
  const [data, setData] = useState({ items: [] });
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/surveys/me").then((r) => setData(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  return (
    <div className="tb-page" data-testid="surveys-page">
      <h1 className="tb-h1">{t("surveys.title")}</h1>
      <p className="tb-muted" style={{ marginTop: 4, marginBottom: 32, maxWidth: 640 }}>{t("surveys.subtitle")}</p>
      {err && <p style={{ color: "#ff7a7a" }}>{err}</p>}
      <div style={{ display: "grid", gap: 14 }}>
        {data.items.length === 0 && <div className="tb-card" style={{ padding: 28 }}><p className="tb-muted">{t("surveys.empty")}</p></div>}
        {data.items.map(({ assignment: a, survey: s }) => (
          <div key={a.id} className="tb-card" style={{ padding: 22 }} data-testid={`survey-row-${s.slug}`}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <h3 style={{ margin: 0, fontSize: 18 }}>{pickL(s, lang, "title")}</h3>
                <p className="tb-muted" style={{ marginTop: 8, marginBottom: 0, fontSize: 13 }}>
                  {s.question_count} {t("surveys.questions")} · ~{s.estimated_minutes} min ·{" "}
                  <span style={{ color: a.status === "completed" ? "#6ee7b7" : "#6eb8ff" }}>
                    {t(`surveys.status.${a.status}`)}
                  </span>
                  {a.completion_percentage > 0 && a.status !== "completed" && (
                    <> · {a.completion_percentage}%</>
                  )}
                </p>
              </div>
              <Link className="tb-primary-btn" to={`/app/surveys/${s.slug}`} data-testid={`open-${s.slug}`}>
                {a.status === "completed" ? t("surveys.review") : a.status === "in_progress" ? t("surveys.continue") : t("surveys.start")}
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Runner */
export function SurveyRunner() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/surveys/${slug}`).then((r) => {
      setSurvey(r.data.survey);
      setAssignment(r.data.assignment);
      setAnswers(r.data.response?.answers || {});
      setDone(r.data.assignment?.status === "completed");
      if (r.data.assignment?.status === "not_started") api.post(`/surveys/${slug}/start`).catch(() => {});
    }).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [slug]);

  const setAns = (qid, v) => {
    setAnswers((a) => ({ ...a, [qid]: v }));
    api.put(`/surveys/${slug}/answer`, { answers: { ...answers, [qid]: v } }).catch(() => {});
  };

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/surveys/${slug}/submit`, { answers });
      setDone(true);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!survey) return <div className="tb-loading">{t("common.loading")}</div>;

  return (
    <div className="tb-page" data-testid="survey-runner">
      <Link to="/app/surveys" className="tb-ghost-btn" style={{ marginBottom: 18, display: "inline-block" }}>← {t("surveys.back")}</Link>
      <h1 className="tb-h1" style={{ marginBottom: 20, lineHeight: 1.25, maxWidth: 720 }}>{pickL(survey, lang, "title")}</h1>
      <p className="tb-muted" style={{ whiteSpace: "pre-line", fontSize: 15, lineHeight: 1.7, maxWidth: 680, marginBottom: 32 }}>{pickL(survey, lang, "intro")}</p>

      {err && <p style={{ color: "#ff7a7a" }}>{err}</p>}

      {done && (
        <div className="tb-card" style={{ marginTop: 12, padding: "36px 32px", borderColor: "#6ee7b7", background: "rgba(110,231,183,0.08)" }} data-testid="survey-done">
          <h3 style={{ marginTop: 0, marginBottom: 16, color: "#6ee7b7", fontSize: 22 }}>✓ {t("surveys.thanks")}</h3>
          <p className="tb-muted" style={{ marginBottom: 24, fontSize: 15, lineHeight: 1.6 }}>{t("surveys.thanksMsg")}</p>
          <Link className="tb-primary-btn" to="/app">{t("surveys.backToApp")}</Link>
        </div>
      )}

      {!done && (
        <>
          <div style={{ display: "grid", gap: 20 }}>
            {survey.questions.map((q, i) => (
              <Question key={q.id} q={q} idx={i + 1} lang={lang} t={t} value={answers[q.id]} onChange={(v) => setAns(q.id, v)} />
            ))}
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 32, marginBottom: 24 }}>
            <button className="tb-primary-btn" onClick={submit} disabled={busy} data-testid="submit-survey">
              {busy ? t("common.saving") : t("surveys.submit")}
            </button>
            <button className="tb-ghost-btn" onClick={() => navigate("/app/surveys")}>{t("surveys.saveExit")}</button>
          </div>
        </>
      )}
    </div>
  );
}

function Question({ q, idx, lang, t, value, onChange }) {
  const label = pickL(q, lang, "label");
  return (
    <div className="tb-card" style={{ padding: "22px 24px" }} data-testid={`q-${q.id}`}>
      <h4 style={{ margin: "0 0 16px", fontSize: 15, lineHeight: 1.45 }}>{idx}. {label}</h4>
      {q.type === "single" && (
        <div style={{ display: "grid", gap: 6 }}>
          {q.options?.map((o) => (
            <label key={o.value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: 4 }}>
              <input type="radio" name={q.id} checked={value === o.value} onChange={() => onChange(o.value)} data-testid={`${q.id}-${o.value}`} />
              {pickL(o, lang)}
            </label>
          ))}
          {q.allow_other && (
            <input className="tb-input" placeholder={t("surveys.other")} value={typeof value === "string" && !q.options?.find((o) => o.value === value) ? value : ""}
              onChange={(e) => onChange(e.target.value)} data-testid={`${q.id}-other`} />
          )}
        </div>
      )}
      {q.type === "multi" && (
        <div style={{ display: "grid", gap: 6 }}>
          <p className="tb-muted" style={{ fontSize: 12, margin: 0 }}>{q.max_choices ? t("surveys.upTo", { n: q.max_choices }) : t("surveys.allThatApply")}</p>
          {q.options?.map((o) => {
            const arr = Array.isArray(value) ? value : [];
            const checked = arr.includes(o.value);
            const atMax = q.max_choices && arr.length >= q.max_choices && !checked;
            return (
              <label key={o.value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: atMax ? "not-allowed" : "pointer", opacity: atMax ? 0.5 : 1, padding: 4 }}>
                <input type="checkbox" checked={checked} disabled={atMax}
                  onChange={() => onChange(checked ? arr.filter((v) => v !== o.value) : [...arr, o.value])}
                  data-testid={`${q.id}-${o.value}`} />
                {pickL(o, lang)}
              </label>
            );
          })}
        </div>
      )}
      {q.type === "rank" && (
        <div>
          <p className="tb-muted" style={{ fontSize: 12, margin: "0 0 8px" }}>{t("surveys.rankHint", { n: q.max_choices || 5 })}</p>
          {q.options?.map((o) => {
            const ranks = (typeof value === "object" && !Array.isArray(value) && value) || {};
            return (
              <div key={o.value} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                <input type="number" min="1" max={q.max_choices || 5} value={ranks[o.value] || ""}
                  style={{ width: 56 }} className="tb-input"
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    const next = { ...ranks };
                    if (!n) delete next[o.value]; else next[o.value] = n;
                    onChange(next);
                  }}
                  data-testid={`${q.id}-${o.value}-rank`} />
                <span>{pickL(o, lang)}</span>
              </div>
            );
          })}
        </div>
      )}
      {q.type === "text" && (
        q.long ? (
          <textarea className="tb-input" rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${q.id}-text`} />
        ) : (
          <input className="tb-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${q.id}-text`} />
        )
      )}
    </div>
  );
}

/* Modal/banner for pending survey shown after login */
export function SurveyPrompt() {
  const { t, lang } = useI18n();
  const [pending, setPending] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const dismissedRecently = sessionStorage.getItem("tb_survey_dismissed_at");
    if (dismissedRecently) return;
    api.get("/surveys/me").then((r) => {
      if (r.data.pending_prompt) setPending(r.data.pending_prompt);
    }).catch(() => {});
  }, []);
  if (!pending) return null;
  const s = pending.survey;
  const close = () => {
    sessionStorage.setItem("tb_survey_dismissed_at", String(Date.now()));
    api.post(`/surveys/${s.slug}/dismiss`).catch(() => {});
    setPending(null);
  };
  return (
    <div className="tb-modal-overlay" data-testid="survey-prompt-modal">
      <div className="tb-modal" style={{ maxWidth: 520, padding: "36px 32px" }}>
        <h3 style={{ marginTop: 0, marginBottom: 18, fontSize: 22, lineHeight: 1.3 }}>{t("surveys.promptTitle")}</h3>
        <p style={{ marginBottom: 14, fontSize: 15, lineHeight: 1.55 }}>{pickL(s, lang, "title")}</p>
        <p className="tb-muted" style={{ fontSize: 13, marginBottom: 0 }}>{t("surveys.promptDuration", { n: s.estimated_minutes || 5 })}</p>
        <div style={{ display: "flex", gap: 10, marginTop: 28 }}>
          <button className="tb-primary-btn" onClick={() => { close(); navigate(`/app/surveys/${s.slug}`); }} data-testid="prompt-start">{t("surveys.startNow")}</button>
          <button className="tb-ghost-btn" onClick={close} data-testid="prompt-skip">{t("surveys.skip")}</button>
        </div>
      </div>
    </div>
  );
}
